/**
 * Spec Phase 10 ADDENDUM §7B.2 — Export bundle modal.
 *
 * Replaces the simple Export ▾ dropdown with a checkbox modal
 * where the drafter chooses which artefacts to include in a
 * single download. Single-item selections download as that
 * file; multi-item selections download as a zipped bundle with
 * a manifest.
 *
 * Jurisdiction + paper overrides are exposed in the modal so
 * a drafter can ship an EP matter as a US-formatted preview
 * without modifying the matter row.
 */
import React, { useEffect, useMemo, useState } from 'react';
import SkippedChecksNote from '@/components/SkippedChecksNote';
import {
  AlertTriangle, Check, Loader2, X, XCircle,
} from 'lucide-react';
import {
  downloadSpecBundle,
  getSpecExportPreflight,
  type SpecExportBundleBody,
  type SpecExportPreflightResponse,
} from '@/api/client';
import { useSpecStore } from '@/store/specStore';

interface Props {
  matterId: string;
  matterJurisdiction?: 'US' | 'EP' | 'IN' | string;
  onClose: () => void;
  onComplete?: (notes: string[]) => void;
}

const _JURISDICTIONS: Array<'EP' | 'US' | 'IN'> = ['EP', 'US', 'IN'];
const _PAPERS: Array<'a4' | 'letter'> = ['a4', 'letter'];

function _matterDefault(value?: string): 'EP' | 'US' | 'IN' {
  const v = (value || '').toUpperCase();
  return _JURISDICTIONS.includes(v as never) ? (v as 'EP' | 'US' | 'IN') : 'EP';
}

function _saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari finishes the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function BundleExportModal({
  matterId, matterJurisdiction, onClose, onComplete,
}: Props): JSX.Element {
  const showToast = useSpecStore((s) => s.showToast);

  const [includeSpecDocx, setIncludeSpecDocx] = useState(true);
  const [includeSpecPdf, setIncludeSpecPdf] = useState(false);
  const [includeDrawingsPdf, setIncludeDrawingsPdf] = useState(true);
  const [includeClaimsDocx, setIncludeClaimsDocx] = useState(false);
  const [includeProvenance, setIncludeProvenance] = useState(true);

  const matterDefault = useMemo(
    () => _matterDefault(matterJurisdiction),
    [matterJurisdiction],
  );
  const [jurisdiction, setJurisdiction] =
    useState<'EP' | 'US' | 'IN'>(matterDefault);
  const paperDefault: 'a4' | 'letter' = jurisdiction === 'US' ? 'letter' : 'a4';
  const [paper, setPaper] = useState<'a4' | 'letter'>(paperDefault);
  // Re-default paper when jurisdiction changes (no manual override
  // hint in v1; explicit user choice will be captured locally).
  const [paperUserPicked, setPaperUserPicked] = useState(false);
  React.useEffect(() => {
    if (!paperUserPicked) {
      setPaper(jurisdiction === 'US' ? 'letter' : 'a4');
    }
  }, [jurisdiction, paperUserPicked]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preflight (Fix 1) — runs on mount + every jurisdiction
  // change. Backend caps the work at the data layer (no DOCX
  // render) so this stays cheap. Failure to fetch is silent —
  // the panel renders an "unavailable" hint but does NOT block
  // export, since a transient network blip shouldn't gate a
  // local file render.
  const [preflight, setPreflight] =
    useState<SpecExportPreflightResponse | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setPreflightLoading(true);
    getSpecExportPreflight(matterId, jurisdiction)
      .then((r) => { if (!cancelled) setPreflight(r); })
      .catch(() => { if (!cancelled) setPreflight(null); })
      .finally(() => { if (!cancelled) setPreflightLoading(false); });
    return () => { cancelled = true; };
  }, [matterId, jurisdiction]);

  // Post-download warnings panel (Fix 2). Replaces the immediate
  // close-on-success behaviour: when the export completes with
  // non-empty warnings, show a small "Review before filing"
  // panel so the drafter can act on margin/paper/word-target
  // drift before sending the file out.
  const [postDownload, setPostDownload] = useState<{
    filename: string;
    warnings: string[];
    notes: string[];
  } | null>(null);

  const anyChecked =
    includeSpecDocx || includeSpecPdf
    || includeDrawingsPdf || includeClaimsDocx;

  const handleSubmit = async (): Promise<void> => {
    if (!anyChecked || submitting) return;
    if (preflight?.blocking) return;
    setSubmitting(true);
    setError(null);
    const body: SpecExportBundleBody = {
      include_spec_docx:    includeSpecDocx,
      include_spec_pdf:     includeSpecPdf,
      include_drawings_pdf: includeDrawingsPdf,
      include_claims_docx:  includeClaimsDocx,
      include_provenance:   includeProvenance,
      jurisdiction_override: jurisdiction,
      paper_override:        paper,
    };
    try {
      const result = await downloadSpecBundle(matterId, body);
      _saveBlob(result.blob, result.filename);
      onComplete?.(result.notes);
      // If the bundle came back with warnings, hold the modal
      // open and surface them in the post-download panel.
      // Otherwise toast + close as before.
      if (result.warnings.length > 0) {
        setPostDownload({
          filename: result.filename,
          warnings: result.warnings,
          notes:    result.notes,
        });
      } else {
        const summary = result.notes.length === 0
          ? `Downloaded ${result.filename}`
          : `Downloaded ${result.filename} (${result.notes.length} notes)`;
        showToast(summary);
        onClose();
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { detail?: { message?: string } } };
        message?: string;
      };
      const msg =
        ax?.response?.data?.detail?.message
        ?? ax?.message
        ?? 'Bundle export failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/40 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="spec-export-bundle-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md border border-zinc-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2">
          <h3 className="text-sm font-medium text-zinc-800">
            Export specification bundle
          </h3>
          <button
            type="button"
            className="rounded-sm p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-4 py-3 text-xs text-zinc-700">
          <PreExportCheck
            loading={preflightLoading}
            preflight={preflight}
            onGoToAbstract={() => {
              // Close + dispatch a section-navigation hint the
              // top-rail listener acts on. Falls through harmlessly
              // when no listener is registered.
              window.dispatchEvent(new CustomEvent(
                'spec.navigate-section', { detail: { section_kind: 'abstract' } },
              ));
              onClose();
            }}
          />
          <div>
            <div className="mb-1 font-medium">What to include?</div>
            <ul className="space-y-1">
              {[
                {
                  k: 'spec_docx',
                  label: 'Specification — DOCX (filing-ready)',
                  v: includeSpecDocx, set: setIncludeSpecDocx,
                },
                {
                  k: 'spec_pdf',
                  label: 'Specification — PDF (render of DOCX)',
                  v: includeSpecPdf, set: setIncludeSpecPdf,
                },
                {
                  k: 'drawings_pdf',
                  label: 'Drawings — USPTO PDF',
                  v: includeDrawingsPdf, set: setIncludeDrawingsPdf,
                },
                {
                  k: 'claims_docx',
                  label: 'Frozen claims — DOCX (standalone)',
                  v: includeClaimsDocx, set: setIncludeClaimsDocx,
                },
                {
                  k: 'provenance',
                  label: 'Provenance appendix in DOCX',
                  v: includeProvenance, set: setIncludeProvenance,
                },
              ].map((row) => (
                <li key={row.k}>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={row.v}
                      onChange={(e) => row.set(e.target.checked)}
                      data-testid={`spec-bundle-toggle-${row.k}`}
                    />
                    <span>{row.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mb-1 font-medium">Jurisdiction</div>
            <div className="flex gap-3">
              {_JURISDICTIONS.map((j) => (
                <label key={j} className="flex cursor-pointer items-center gap-1">
                  <input
                    type="radio"
                    name="bundle-jurisdiction"
                    value={j}
                    checked={jurisdiction === j}
                    onChange={() => {
                      setJurisdiction(j);
                      setPaperUserPicked(false);
                    }}
                    data-testid={`spec-bundle-jurisdiction-${j}`}
                  />
                  <span>{j}</span>
                  {j === matterDefault && (
                    <span className="text-zinc-400">(matter)</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1 font-medium">Paper</div>
            <div className="flex gap-3">
              {_PAPERS.map((p) => (
                <label key={p} className="flex cursor-pointer items-center gap-1">
                  <input
                    type="radio"
                    name="bundle-paper"
                    value={p}
                    checked={paper === p}
                    onChange={() => {
                      setPaper(p);
                      setPaperUserPicked(true);
                    }}
                    data-testid={`spec-bundle-paper-${p}`}
                  />
                  <span>{p === 'a4' ? 'A4' : 'US Letter'}</span>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div
              className="rounded-sm border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700"
              role="alert"
              data-testid="spec-bundle-error"
            >
              {error}
            </div>
          )}

          {postDownload && (
            <PostDownloadPanel
              filename={postDownload.filename}
              warnings={postDownload.warnings}
              onDone={() => { setPostDownload(null); onClose(); }}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-2">
          <button
            type="button"
            className="rounded-sm border border-zinc-300 bg-white px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={
              preflight && !preflight.blocking
                  && preflight.warnings.length > 0
                ? 'inline-flex items-center gap-1 rounded-sm border border-amber-500 '
                  + 'bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 '
                  + 'hover:bg-amber-100 disabled:cursor-not-allowed '
                  + 'disabled:opacity-50'
                : 'inline-flex items-center gap-1 rounded-sm bg-zinc-900 '
                  + 'px-3 py-1 text-xs font-medium text-white hover:bg-zinc-800 '
                  + 'disabled:cursor-not-allowed disabled:opacity-50'
            }
            onClick={handleSubmit}
            disabled={
              !anyChecked
              || submitting
              || preflight?.blocking === true
              || postDownload !== null
            }
            title={
              preflight?.blocking
                ? 'Fix blocking issues before exporting.'
                : undefined
            }
            data-testid="spec-bundle-submit"
          >
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
            {preflight && !preflight.blocking
                && preflight.warnings.length > 0
              ? 'Export with warnings'
              : 'Export selected →'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Pre-export check panel (Fix 1) ────────────────────────────
function PreExportCheck({
  loading,
  preflight,
  onGoToAbstract,
}: {
  loading: boolean;
  preflight: SpecExportPreflightResponse | null;
  onGoToAbstract: () => void;
}): JSX.Element | null {
  if (loading) {
    return (
      <div
        className="flex items-center gap-2 rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-zinc-500"
        data-testid="spec-bundle-preflight-loading"
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Running checks…
      </div>
    );
  }
  if (!preflight) {
    return (
      <div className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-zinc-500">
        Pre-export checks unavailable. Export will proceed without
        a preflight summary.
      </div>
    );
  }
  const errors = preflight.warnings.filter((w) => w.severity === 'error');
  const advisories = preflight.warnings.filter((w) => w.severity === 'warning');
  const hasUsAbstractTooLong = errors.some(
    (w) => w.slug === 'us_abstract_too_long',
  );
  const skipped = preflight.checks_skipped || [];
  if (errors.length === 0 && advisories.length === 0) {
    return (
      <div className="space-y-1">
        <div
          className="flex items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-emerald-700"
          data-testid="spec-bundle-preflight-ok"
        >
          <Check className="h-3.5 w-3.5" aria-hidden />
          {/* Not "all checks passed" when some did not run. */}
          {skipped.length > 0 ? 'No issues in the checks that ran.' : 'All checks passed.'}
        </div>
        <SkippedChecksNote skipped={skipped} testId="spec-bundle-preflight-skipped" />
      </div>
    );
  }
  return (
    <div
      className="space-y-1 rounded-sm border border-zinc-200 bg-white px-2 py-1.5"
      data-testid="spec-bundle-preflight-panel"
    >
      <div className="text-[11px] font-medium text-zinc-700">
        Pre-export check
      </div>
      {advisories.length > 0 && (
        <div data-testid="spec-bundle-preflight-warnings">
          <div className="flex items-center gap-1 text-amber-700">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            <span className="font-medium">
              {advisories.length} warning{advisories.length === 1 ? '' : 's'}
              {' '}before export:
            </span>
          </div>
          <ul className="ml-4 list-disc text-zinc-700">
            {advisories.map((w, i) => (
              <li key={`${w.slug}-${i}`}>
                {w.message}
                {w.detail && <span className="text-zinc-500"> {w.detail}</span>}
                {w.legal_ref && (
                  <span className="text-zinc-500" data-testid={`spec-bundle-preflight-ref-${w.slug}`}>
                    {' '}({w.legal_ref})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {errors.length > 0 && (
        <div data-testid="spec-bundle-preflight-errors">
          <div className="flex items-center gap-1 text-rose-700">
            <XCircle className="h-3 w-3" aria-hidden />
            <span className="font-medium">
              {errors.length} blocking issue{errors.length === 1 ? '' : 's'}:
            </span>
          </div>
          <ul className="ml-4 list-disc text-zinc-700">
            {errors.map((w, i) => (
              <li key={`${w.slug}-${i}`}>
                {w.message}
                {w.detail && <span className="text-zinc-500"> {w.detail}</span>}
                {w.legal_ref && (
                  <span className="text-zinc-500" data-testid={`spec-bundle-preflight-ref-${w.slug}`}>
                    {' '}({w.legal_ref})
                  </span>
                )}
              </li>
            ))}
          </ul>
          {hasUsAbstractTooLong && (
            <button
              type="button"
              className="mt-1 text-rose-700 underline hover:text-rose-900"
              onClick={onGoToAbstract}
              data-testid="spec-bundle-preflight-goto-abstract"
            >
              Go to Abstract →
            </button>
          )}
        </div>
      )}
      <SkippedChecksNote skipped={skipped} testId="spec-bundle-preflight-skipped" />
    </div>
  );
}


// ─── Post-download warnings panel (Fix 2) ─────────────────────
function PostDownloadPanel({
  filename, warnings, onDone,
}: {
  filename: string;
  warnings: string[];
  onDone: () => void;
}): JSX.Element {
  return (
    <div
      className="space-y-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-2"
      data-testid="spec-bundle-post-download"
    >
      <div className="font-medium text-zinc-800">
        Downloaded: {filename}
      </div>
      <div className="text-[11px] text-zinc-600">Review before filing:</div>
      <ul className="ml-4 list-disc text-zinc-700">
        {warnings.map((w, i) => (
          <li key={i}>{humaniseExportWarning(w)}</li>
        ))}
      </ul>
      <div className="flex justify-end pt-1">
        <button
          type="button"
          className="rounded-sm border border-zinc-300 bg-white px-2 py-0.5 text-zinc-700 hover:bg-zinc-50"
          onClick={onDone}
          data-testid="spec-bundle-post-download-done"
        >
          Done
        </button>
      </div>
    </div>
  );
}

export function humaniseExportWarning(slug: string): string {
  // Best-effort copy for the most common slugs; unknown
  // slugs render as-is so nothing is silently dropped.
  if (slug.startsWith('below_word_target:')) {
    const detail = slug.split(':', 2)[1] || '';
    return `Below word target — ${detail}`;
  }
  if (slug.startsWith('open_examiner_flags:')) {
    const n = slug.split(':', 2)[1] || '?';
    return `${n} open examiner flag${n === '1' ? '' : 's'}`;
  }
  if (slug.startsWith('idf_concepts_missing:')) {
    const n = slug.split(':', 2)[1] || '?';
    return `${n} IDF concept${n === '1' ? '' : 's'} not found in the draft `
      + '(listed in the export appendix).';
  }
  if (slug === 'ep_line_numbering_missing') {
    return 'EP line numbering not enabled in margins.';
  }
  if (slug === 'us_unexpected_industrial_applicability') {
    return 'US export contains an industrial-applicability section.';
  }
  if (slug === 'us_unexpected_reference_signs_list') {
    return 'US export contains a reference-signs list.';
  }
  if (slug === 'in_claims_lead_in_missing') {
    return 'IN claims lead-in ("We Claim:" / "I Claim:") not found.';
  }
  if (slug.startsWith('paper_mismatch:')) {
    return `Paper size drift — ${slug.split(':', 2)[1] || ''}`;
  }
  if (slug.startsWith('margin_mismatch:')) {
    return `Margin drift — ${slug.split(':', 2)[1] || ''}`;
  }
  if (slug.startsWith('claims_lead_in_missing:')) {
    return `Expected claims lead-in not found (${slug.split(':', 2)[1] || ''}).`;
  }
  return slug;
}
